import React, { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { KeyRound, Mail } from "lucide-react";

export const AdminResetPassword = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "email") {
        const { data } = await api.post(`/users/${user.id}/send-reset`);
        toast.success(data.message || "Reset link sent");
      } else {
        await api.post(`/users/${user.id}/password`, { password: pw });
        toast.success(`Temporary password set for ${user.name}. Share it with them privately.`);
      }
      setOpen(false);
      setPw("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-sm h-8 text-xs" data-testid={`reset-password-${user.email}`}>
          <KeyRound className="w-3.5 h-3.5 mr-1" />Reset Password
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm bg-card max-w-md" data-testid="admin-reset-dialog">
        <DialogHeader>
          <DialogTitle>Reset password for {user.name}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="border border-border rounded-sm p-4">
            <p className="text-sm font-medium mb-1">Email a reset link</p>
            <p className="text-xs text-muted-foreground mb-3">They choose their own new password. Link expires in 1 hour.</p>
            <Button size="sm" disabled={!!busy} onClick={() => run("email")} data-testid="admin-send-reset-link"
              className="rounded-sm bg-black hover:bg-neutral-800 text-white">
              <Mail className="w-3.5 h-3.5 mr-1.5" />{busy === "email" ? "Sending…" : "Send reset link"}
            </Button>
          </div>
          <div className="border border-border rounded-sm p-4">
            <p className="text-sm font-medium mb-1">Set a temporary password</p>
            <p className="text-xs text-muted-foreground mb-3">Share it with them privately; they can change it after signing in.</p>
            <Label className="text-xs uppercase tracking-[0.15em]">Temporary Password</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="8+ characters"
                data-testid="admin-temp-password-input" className="rounded-sm h-9" />
              <Button size="sm" variant="outline" disabled={!!busy || pw.length < 8} onClick={() => run("temp")}
                data-testid="admin-set-temp-password" className="rounded-sm h-9">
                {busy === "temp" ? "Saving…" : "Set"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
